import type { Hero, Level } from "./types";

/**
 * The guild's cast, in joining order — the bench grows by one hero per day
 * (day 1 fields the first three). Stats are [might, lore, guile], 1..9.
 * Sera is the deliberate greedy bait: the best all-rounder, whose obvious
 * "send her to the richest contract" play is exactly what days 2–5 punish.
 */
export const CAST: Hero[] = [
  { name: "Sera Brightblade", emoji: "⚔️", stats: [9, 3, 4] },
  { name: "Maelis the Violet", emoji: "🔮", stats: [1, 9, 2] },
  { name: "Pip Underfoot", emoji: "🗝️", stats: [2, 1, 9] },
  { name: "Bram Oakshield", emoji: "🛡️", stats: [7, 1, 2] },
  { name: "Wren Whisperwind", emoji: "🏹", stats: [4, 2, 7] },
  { name: "Tobben Greymantle", emoji: "📜", stats: [2, 7, 4] },
  { name: "Esme the Lark", emoji: "🎻", stats: [5, 5, 5] },
];

/**
 * The five days, ramping from 3 to 7 contracts. A contract pays
 * `base + weights·stats` gold to whichever hero takes it. The slates come
 * from the kept design tool (`scripts/tune-levels.ts`, seeds 0/1/1/60/74):
 * every `opt` is the proven unique maximum (Hungarian + exhaustive search +
 * real HiGHS in levels.test.ts), every `greedy` is the deterministic
 * best-pair-first baseline. Day 1 is friendly (greedy = opt); days 2–5 leave
 * greedy 15 / 16 / 33 / 23 gold short (4.0 / 3.5 / 6.1 / 4.1%) — the obvious
 * play earns two stars, never three.
 */
export const LEVELS: Level[] = [
  {
    name: "The Morning Post",
    heroCount: 3,
    quests: [
      { name: "Wyvern on the West Road", emoji: "🐉", base: 20, weights: [6, 0, 0] },
      { name: "The Misprinted Grimoire", emoji: "📜", base: 25, weights: [1, 9, 1] },
      { name: "The Locked Cellar", emoji: "🍷", base: 25, weights: [1, 0, 6] },
    ],
    opt: 264,
    greedy: 264,
  },
  {
    name: "The Crowded Bench",
    heroCount: 4,
    quests: [
      { name: "Wolves at the Sheepfold", emoji: "🐺", base: 15, weights: [9, 0, 2] },
      { name: "A Curse on the Mill", emoji: "🌾", base: 30, weights: [1, 10, 0] },
      { name: "The Magpie's Ransom", emoji: "💎", base: 30, weights: [0, 1, 6] },
      { name: "Watch on the Old Wall", emoji: "🏰", base: 30, weights: [3, 0, 6] },
    ],
    opt: 373,
    greedy: 358,
  },
  {
    name: "The Gilded Contracts",
    heroCount: 5,
    quests: [
      { name: "The Ember Drake", emoji: "🔥", base: 20, weights: [10, 0, 2] },
      { name: "The Sunken Library", emoji: "🌊", base: 35, weights: [2, 7, 2] },
      { name: "The Counting-House Job", emoji: "🪙", base: 15, weights: [1, 2, 8] },
      { name: "The Haunting at Hollowmere", emoji: "🌫️", base: 20, weights: [1, 4, 5] },
      { name: "Escort to Greyford", emoji: "🐎", base: 30, weights: [4, 1, 6] },
    ],
    opt: 451,
    greedy: 435,
  },
  {
    name: "The King's Commission",
    heroCount: 6,
    quests: [
      { name: "The King's Escort", emoji: "👑", base: 40, weights: [9, 0, 1] },
      { name: "The Basilisk of Briarfen", emoji: "🐍", base: 35, weights: [4, 5, 1] },
      { name: "Spiders in the Tin Mine", emoji: "🕸️", base: 30, weights: [8, 0, 1] },
      { name: "The Salamander Forge", emoji: "⚒️", base: 15, weights: [2, 7, 0] },
      { name: "The Masquerade Heist", emoji: "🎭", base: 40, weights: [1, 1, 6] },
      { name: "The Alchemist's Debt", emoji: "🧪", base: 25, weights: [1, 4, 4] },
    ],
    opt: 537,
    greedy: 504,
  },
  {
    name: "The Grand Muster",
    heroCount: 7,
    quests: [
      { name: "The Dragon of Cinder Pass", emoji: "🐉", base: 30, weights: [9, 0, 2] },
      { name: "The Siege of Farrow Keep", emoji: "🏰", base: 15, weights: [7, 1, 2] },
      { name: "The Oracle's Riddle", emoji: "👁️", base: 15, weights: [0, 6, 0] },
      { name: "The Moonwell Rite", emoji: "🌙", base: 30, weights: [0, 6, 2] },
      { name: "The Vault of Nine Locks", emoji: "🔒", base: 15, weights: [0, 1, 7] },
      { name: "The Smugglers' Quay", emoji: "⚓", base: 25, weights: [1, 0, 6] },
      { name: "The Witch-Road Patrol", emoji: "🌲", base: 20, weights: [5, 1, 6] },
    ],
    opt: 567,
    greedy: 544,
  },
];
